import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { openDataUrl } from "@/lib/openDataUrl";
import { ArrowLeft, MapPin, Phone, Mail, ArrowUpRight, Droplets, Calendar, UserCog, Send, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import PaymentMethodCard from "@/components/PaymentMethodCard";

// --- Small helpers to replace nested ternaries ---
const getPortalStatus = (activated, invitedAt) => {
  if (activated) return { cls: "bg-success/10 text-success", label: "Activated" };
  if (invitedAt) return { cls: "bg-primary/10 text-primary", label: "Invited, not activated" };
  return { cls: "bg-secondary text-muted-foreground", label: "No invite sent" };
};
const getInviteButtonLabel = (busy, invitedAt) => {
  if (busy) return "Sending…";
  return invitedAt ? "Re-send invitation" : "Send portal invitation";
};

function PortalManager({ customer, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(customer.portal_enabled !== false);
  const portalEmail = (customer.portal_email || "").trim();
  const activated = !!customer.portal_activated_at;
  const invitedAt = customer.portal_invited_at;
  const inviteEmailOk = customer.portal_invite_email_ok;

  const toggle = async (v) => {
    setEnabled(v);
    try {
      await api.patch(`/customers/${customer.id}/portal/enabled`, { enabled: v });
      toast.success(v ? "Portal enabled" : "Portal disabled — customer cannot log in");
      onChanged && onChanged();
    } catch (e) {
      setEnabled(!v);
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const sendInvite = async () => {
    if (!portalEmail) return toast.error("Set a portal email on the customer record first");
    setBusy(true);
    try {
      const r = await api.post(`/customers/${customer.id}/portal/invite`);
      if (r.data?.email_sent) {
        toast.success(`Invitation sent to ${r.data.email}`);
      } else {
        if (r.data?.invite_link && navigator.clipboard) {
          try { await navigator.clipboard.writeText(r.data.invite_link); }
          catch (clipErr) { logError("Clipboard write blocked:", clipErr); /* toast below still shows the reason */ }
        }
        toast.message(
          `Email failed: ${r.data?.email_error || "unknown error"}. The invite link was copied to your clipboard — you can text or WhatsApp it to the customer (valid 72h).`,
          { duration: 12000 }
        );
      }
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const portalStatus = getPortalStatus(activated, invitedAt);

  return (
    <section className="border border-border" data-testid="portal-manager">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <UserCog className="w-4 h-4 text-primary" />
          <h2 className="font-display text-xl font-bold tracking-tight">Customer portal access</h2>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${portalStatus.cls}`} data-testid="portal-status-pill">
          {portalStatus.label}
        </span>
      </div>
      <div className="px-6 py-5 grid md:grid-cols-2 gap-6 items-start">
        <div className="space-y-3">
          <div>
            <div className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">Portal email</div>
            <div className="text-sm font-medium" data-testid="portal-email-display">
              {portalEmail || <span className="text-muted-foreground italic">Not set — add one in Edit customer to enable portal access.</span>}
            </div>
          </div>
          {invitedAt && (
            <div className="text-xs text-muted-foreground font-mono">
              Last invite: {new Date(invitedAt).toLocaleString()}
              {inviteEmailOk === false && <span className="ml-2 text-destructive">(email send failed — link still works)</span>}
            </div>
          )}
          {activated && customer.portal_activated_at && (
            <div className="text-xs text-success font-mono">
              Activated on {new Date(customer.portal_activated_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <label className="flex items-start gap-3 border border-border rounded-sm p-3 bg-secondary/30 cursor-pointer">
            <Switch checked={enabled} onCheckedChange={toggle} data-testid="portal-enabled-switch" />
            <div>
              <div className="font-semibold text-sm flex items-center gap-1">
                <Power className="w-3 h-3" /> Portal login {enabled ? "enabled" : "disabled"}
              </div>
              <p className="text-xs text-muted-foreground">When disabled, the customer cannot sign in even with the correct password.</p>
            </div>
          </label>
          <Button
            onClick={sendInvite}
            disabled={!portalEmail || busy}
            className="w-full rounded-sm"
            data-testid="portal-invite-button"
          >
            <Send className="w-4 h-4 mr-2" /> {getInviteButtonLabel(busy, invitedAt)}
          </Button>
          {portalEmail && (
            <p className="text-[11px] text-muted-foreground font-mono leading-snug">
              The customer will receive an email at <strong>{portalEmail}</strong> with a link to set their password and activate their account. Link is valid for 72 hours.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, sub, testid, tone }) {
  const toneClass = {
    paid: "text-success",
    out: "text-destructive",
    life: "text-primary",
    neutral: "text-foreground",
  }[tone || "neutral"];
  return (
    <div className="border border-border p-6 bg-background" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-display text-3xl sm:text-4xl font-black tracking-tighter mt-3 mono-num ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2 font-mono">{sub}</div>}
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => api.get(`/customers/${id}/summary`).then((r) => setSummary(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);

  if (!summary) return <div className="overline">Loading…</div>;

  const { customer: c, invoices, totals } = summary;

  return (
    <div className="space-y-6 animate-fade-up">
      <Link to="/admin/customers" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-customers">
        <ArrowLeft className="w-4 h-4 mr-2" /> All customers
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Customer</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1" data-testid="customer-name">
            {c.business_name || c.name}
          </h1>
          {c.business_name && <p className="text-sm text-muted-foreground mt-1">{c.name}</p>}
          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground flex-wrap">
            {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
            {c.contact_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.contact_phone}</span>}
            {c.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.address}</span>}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground font-mono uppercase tracking-wider">
            <span data-testid="customer-schedule-label">
              {(c.schedule || "on call").replace("_", " ")}
              {Number.isInteger(c.schedule_day) && c.schedule_day >= 0 && c.schedule_day <= 6 && (
                <span> · {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][c.schedule_day]}</span>
              )}
            </span>
            {totals.last_delivery_at && <span>· Last delivery {fmtDate(totals.last_delivery_at)}</span>}
            {totals.last_paid_at && <span>· Last paid {fmtDate(totals.last_paid_at)}</span>}
          </div>
          {c.tax_exempt && (
            <div className="mt-3 inline-flex items-center gap-2 border border-success/30 bg-success/10 rounded-sm px-2 py-1 flex-wrap" data-testid="customer-tax-exempt-banner">
              <span className="text-[10px] font-bold uppercase tracking-wider text-success">Tax exempt</span>
              {c.tax_exempt_number && <span className="text-xs font-mono text-success">· {c.tax_exempt_number}</span>}
              {c.tax_exempt_form_data_url && (
                <button type="button" onClick={() => openDataUrl(c.tax_exempt_form_data_url)} className="text-xs text-success hover:underline" data-testid="customer-tax-exempt-cert-link">
                  view ST-119.4
                </button>
              )}
              {c.tax_exempt_form_data_url_2 && (
                <button type="button" onClick={() => openDataUrl(c.tax_exempt_form_data_url_2)} className="text-xs text-success hover:underline" data-testid="customer-tax-exempt-cert-link-2">
                  view tax exempt form
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Financial summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-border">
        <div className="border-r border-border last:border-r-0">
          <Stat testid="cust-stat-outstanding" tone="out" label="Outstanding" value={fmtMoney(totals.total_outstanding)} sub={`${totals.unpaid_count} unpaid invoice${totals.unpaid_count === 1 ? "" : "s"}`} />
        </div>
        <div className="border-r border-border last:border-r-0">
          <Stat testid="cust-stat-paid" tone="paid" label="Total paid" value={fmtMoney(totals.total_paid)} sub={`${totals.paid_count} paid invoice${totals.paid_count === 1 ? "" : "s"}`} />
        </div>
        <div className="border-r border-border last:border-r-0">
          <Stat testid="cust-stat-lifetime" tone="life" label="Lifetime revenue" value={fmtMoney(totals.total_lifetime)} sub="paid + unpaid" />
        </div>
        <Stat testid="cust-stat-gallons" tone="neutral" label="Gallons delivered"
              value={<><span>{Math.round(totals.total_gallons)}</span><span className="text-lg font-bold ml-1 text-muted-foreground">gal</span></>}
              sub={`across ${totals.invoice_count} invoice${totals.invoice_count === 1 ? "" : "s"}`} />
      </div>

      {/* Payment method on file */}
      <section className="border border-border p-4 sm:p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold tracking-tight">Payment method</h2>
        </div>
        <PaymentMethodCard
          customer={{
            id: c.id,
            card_on_file: c.card_on_file,
            auto_charge_on_close: c.auto_charge_on_close,
          }}
          onChange={load}
          testIdPrefix="customer-pm"
        />
      </section>

      {/* Portal access */}
      <PortalManager customer={c} onChanged={load} />

      {/* All invoices */}
      <section className="border border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-display text-xl font-bold tracking-tight">All invoices</h2>
          <Link to="/admin/invoices" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="link-all-invoices">
            View all invoices <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
        <div className="min-w-[700px]">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline">
          <div className="col-span-2">Number</div>
          <div className="col-span-3">Delivery</div>
          <div className="col-span-2">Gallons</div>
          <div className="col-span-2 text-right">Total</div>
          <div className="col-span-2 text-right">Status</div>
          <div className="col-span-1"></div>
        </div>
        {invoices.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">No invoices yet for this customer.</div>
        )}
        {invoices.map((inv) => (
          <Link to={`/admin/invoices/${inv.id}`} key={inv.id} className="grid grid-cols-12 px-6 py-3 border-b border-border last:border-b-0 hover:bg-secondary/40 items-center" data-testid={`cust-invoice-${inv.number}`}>
            <div className="col-span-2 mono-num text-sm font-semibold">{inv.number}</div>
            <div className="col-span-3 text-sm flex items-center gap-1 text-muted-foreground mono-num"><Calendar className="w-3 h-3" /> {fmtDate(inv.delivery_date)}</div>
            <div className="col-span-2 text-sm flex items-center gap-1 mono-num"><Droplets className="w-3 h-3 text-primary" /> {Math.round(Number(inv.gallons) || 0)}</div>
            <div className="col-span-2 text-right mono-num text-sm">{fmtMoney(inv.total)}</div>
            <div className="col-span-2 text-right">
              <span className={`text-xs font-bold uppercase tracking-wider ${inv.status === "paid" ? "text-success" : "text-destructive"}`}>{inv.status}</span>
            </div>
            <div className="col-span-1 text-right text-muted-foreground"><ArrowUpRight className="w-4 h-4 inline" /></div>
          </Link>
        ))}
        </div>
        </div>
      </section>
    </div>
  );
}
